/**
 * Store: Index
 * -----------------------------------------------------------------------------
 * Root store for all theme level states.
 * - Tied to the index component.
 * - Only JSDoc custom parameters, ignore module state or object.
 *
 */
export default {
  state: () => ({
    hydrated: false,
    notification: false,
    previousUrl: '/',
    screen: {
      breakpoint: 'xs',
      height: 0,
      mobile: true,
      width: 0,
    },
    scroll: {
      direction: 'none',
      header: 'open',
      paused: false,
      top: true,
      x: 0,
      y: 0,
    },
    settings: {
      mobileBreakpoints: ['xs', 's', 'm'],
      rtlLanguages: ['ar', 'dv', 'fa', 'ff', 'he', 'ks', 'ku', 'ur', 'wo', 'yi'],
      start: 100,
      threshold: 5,
      throttle: 250,
    },
    stylesheets: [],
  }),

  getters: {

    /**
     * Get the current active notification's object.
     * @returns {Boolean|Object}
     */
    getActiveNotification(state) {
      return state.notification
    },

    /**
     * Get if stylesheet is in stylesheets.
     * - Does not need to be complete match to be true.
     * @param {String} stylesheet - Stylesheet to test.
     * @returns {Boolean}
     */
    getLoadedStylesheet: (state) => (stylesheet) => {
      return state.stylesheets.some((loadedStylesheet) => {
        return loadedStylesheet.includes(stylesheet)
      })
    },

    /**
     * Get if RTL writing direction.
     * @returns {Boolean}
     */
    getRtlWritingDirection(state) {
      return state.settings.rtlLanguages.includes(cnvs.store.language)
    },
  },

  mutations: {

    /**
     * Set rehydrated state.
     */
    SET_HYDRATED(state) {
      state.hydrated = true
    },

    /**
     * Set mobile breakpoints state.
     * @param {Array} mobileBreakpoints - Breakpoints considered as mobile.
     */
    SET_MOBILE_BREAKPOINTS(state, mobileBreakpoints) {
      state.settings.mobileBreakpoints = mobileBreakpoints
    },

    /**
     * Set notification state.
     * @param {Object} notification - Notification to display.
     */
    SET_NOTIFICATION(state, notification) {
      state.notification = notification
    },

    /**
     * Set paused scroll state.
     * @param {Object} paused - Paused scroll state.
     */
    SET_PAUSED_SCROLL(state, paused) {
      state.scroll.paused = paused
    },

    /**
     * Set previous URL state.
     * @param {String} pathname - Previous pathname.
     */
    SET_PREVIOUS_URL(state, pathname) {
      state.previousUrl = pathname
    },

    /**
     * Set screen state.
     * @param {Object} screen - Screen state object.
     */
    SET_SCREEN(state, screen) {
      state.screen = screen
    },

    /**
     * Set scroll state.
     * - Does not update paused state.
     * @param {Object} scroll - Scroll state object.
     */
    SET_SCROLL(state, scroll) {
      state.scroll = {
        ...scroll,
        paused: state.scroll.paused,
      }
    },

    /**
     * Set scroll start.
     * @param {String} start - New scroll start.
     */
    SET_SCROLL_START(state, start) {
      state.settings.start = start
    },

    /**
     * Set loaded stylesheets.
     * @param {String} stylesheet - Loaded stylesheet file name.
     */
    SET_STYLESHEET(state, stylesheet) {
      state.stylesheets = [
        ...state.stylesheets,
        stylesheet,
      ]
    },
  },

  actions: {

    /**
     * Commit rehydrated state.
     * - Runs initial items.
     */
    async rehydrateSite({ dispatch, commit }) {
      commit('SET_HYDRATED')

      await Promise.all([dispatch('cart/setResponse', true)])
    },

    /**
     * Commit mobile breakpoints state.
     * @param {Array} mobileBreakpoints - Breakpoints considered as mobile.
     */
    setMobileBreakpoints({ commit }, mobileBreakpoints) {
      commit('SET_MOBILE_BREAKPOINTS', mobileBreakpoints)
    },

    /**
     * Commit notification state.
     * @param {Boolean|Object} notification - Notification to display, see
     * notification-panel.vue for props, accepts false to hide.
     * @param {String} displayUrl - Pathname URL to display notification on
     * after setting notification, it's recommended you use Shopify routes to
     * automatically account for Market sub-domains.
     */
    setNotification({ commit }, notification = {}) {
      commit('SET_NOTIFICATION', notification)
    },

    /**
     * Commit paused scroll state.
     * @param {Object} paused - Paused scroll state.
     */
    setPausedScroll({ commit }, paused) {
      commit('SET_PAUSED_SCROLL', paused)
    },

    /**
     * Commit previous URL state.
     * @param {String} pathname - Previous pathname.
     */
    setPreviousUrl({ commit }, pathname) {
      commit('SET_PREVIOUS_URL', pathname)
    },

    /**
     * Commit screen state.
     * @param {Object} screen - Screen state object.
     */
    setScreen({ commit }, screen) {
      commit('SET_SCREEN', screen)
    },

    /**
     * Commit scroll state.
     * @param {Object} scroll - Scroll state object.
     */
    setScroll({ commit }, scroll) {
      commit('SET_SCROLL', scroll)
    },

    /**
     * Commit scroll start setting state.
     * @param {String} start - New start.
     */
    setScrollStart({ commit }, start) {
      commit('SET_SCROLL_START', start)
    },

    /**
     * Commit loaded stylesheet state.
     * @param {String} stylesheet - Loaded stylesheet file name.
     */
    setStylesheet({ commit }, stylesheet) {
      commit('SET_STYLESHEET', stylesheet)
    },
  },
}
